/* eslint-disable react/jsx-no-undef */
import { IconButton } from '@digibee/beehive-ui';
import { choose, iff, otherwise, when } from '@digibee/control-statements';
import {
  Book,
  CircleSpark,
  Gift,
  GraduationCap,
  Headset,
  HelpCircle,
  MessageText,
  PageEdit
} from 'iconoir-react';
import { FC } from 'react';

import * as Elements from './Actions.elements';

import i18n from '~/common/helpers/i18n';
import Account from '~/components/Account';
import Badge from '~/components/Badge';
import Text from '~/components/Text';
import Tooltip from '~/components/Tooltip';

interface Props {
  unreadCount: number;
  onToggle: () => void;
  eula: any;
  openEula: () => void;
  openSupportAccess: () => void;
  enabledSupportAccess: boolean;
}

const Actions: FC<Props> = ({
  unreadCount,
  onToggle,
  eula,
  openEula,
  openSupportAccess,
  enabledSupportAccess
}) => (
  <Elements.Container>
    <Elements.HelpMenu
      left='-175px'
      width={200}
      zIndexValue={50}
      component={({ toggle }) => (
        <Elements.Wrapper>
          {choose(
            when(eula && !eula?.acceptanceDate, () => (
              <Tooltip
                position='bottom'
                align='left'
                content={i18n.t('scenes.eula.labels.license_term_alert')}
              >
                <IconButton
                  onClick={toggle}
                  size='medium'
                  data-testid='button-actions'
                >
                  {iff(
                    unreadCount > 0 || (eula && !eula?.acceptanceDate),
                    () => (
                      <Elements.Notification />
                    )
                  )}
                  <HelpCircle />
                </IconButton>
              </Tooltip>
            )),
            otherwise(() => (
              <IconButton
                onClick={toggle}
                size='medium'
                data-testid='button-actions'
              >
                {iff(unreadCount > 0 || (eula && !eula?.acceptanceDate), () => (
                  <Elements.Notification />
                ))}
                <HelpCircle />
              </IconButton>
            ))
          )}
        </Elements.Wrapper>
      )}
    >
      <Elements.List inset noHairlines>
        <Elements.Item
          data-testid='help-menu-link-intercom-chat'
          onClick={() => onToggle()}
        >
          <Elements.Flex>
            <Elements.Box>
              <MessageText className='mr-2' />
              <Text>{i18n.t('scenes.beta.actions.open_chat')}</Text>
            </Elements.Box>
            <Elements.Box>
              {iff(unreadCount > 0, () => (
                <Badge type='info'>
                  {unreadCount}{' '}
                  {unreadCount === 1
                    ? i18n.t('label.message')
                    : i18n.t('label.messages')}
                </Badge>
              ))}
            </Elements.Box>
          </Elements.Flex>
        </Elements.Item>

        <Elements.Link
          data-testid='help-menu-link-intercom-academy'
          href={i18n.t('action.digibee_academy_link_url')}
          target='_blank'
        >
          <Elements.Item>
            <GraduationCap className='mr-2' />
            <Text align='left'>{i18n.t('label.digibee_academy_link')}</Text>
          </Elements.Item>
        </Elements.Link>

        <Elements.Link
          href={i18n.t('action.documentation_url')}
          data-testid='help-menu-link-intercom-documentation'
          target='_blank'
        >
          <Elements.Item>
            <Book className='mr-2' />
            <Text align='left'>{i18n.t('action.documentation')}</Text>
          </Elements.Item>
        </Elements.Link>

        <Elements.Link
          data-testid='help-menu-link-ai-assistant-academy'
          href={i18n.t('action.ai_assistant_url')}
          target='_blank'
        >
          <Elements.Item>
            <CircleSpark className='mr-2' />
            <Text align='left'>{i18n.t('action.ai_assistant')}</Text>
            <Badge style={{ marginLeft: '10px' }}>Beta</Badge>
          </Elements.Item>
        </Elements.Link>

        <Elements.Link
          data-testid='help-menu-link-intercom-release-notes'
          href={i18n.t('scenes.beta.actions.news_url')}
          target='_blank'
        >
          <Elements.Item>
            <Gift className='mr-2' />
            <Text>{i18n.t('scenes.beta.actions.open_news')}</Text>
          </Elements.Item>
        </Elements.Link>
        <Elements.Link
          target='_blank'
          href='https://digibee.atlassian.net/servicedesk/customer/portal/6'
          data-testid='help-menu-link-intercom-open-send-feedback'
        >
          <Elements.Item>
            <PageEdit className='mr-2' />
            <Text>{i18n.t('scenes.beta.actions.send_feedback')}</Text>
          </Elements.Item>
        </Elements.Link>
        <Elements.Item
          data-testid='help-menu-link-open-support-access'
          onClick={() => openSupportAccess()}
        >
          <Headset className='mr-2' />
          <Text>
            {enabledSupportAccess
              ? i18n.t('action.support_access_on')
              : i18n.t('action.support_access_off')}
          </Text>
        </Elements.Item>
        {iff(eula, () => (
          <Elements.Item
            data-testid='help-menu-link-intercom-open-eula1'
            onClick={openEula}
          >
            <Elements.Flex>
              <Elements.Box>
                <PageEdit className='mr-2' />
                <Text>{i18n.t('scenes.eula.labels.license_term')}</Text>
              </Elements.Box>
              <Elements.Box>
                {iff(
                  !eula?.acceptanceDate && eula?.lastPostponementDate,
                  () => (
                    <Badge type='danger'>{eula?.countDown}</Badge>
                  )
                )}
              </Elements.Box>
            </Elements.Flex>
          </Elements.Item>
        ))}
      </Elements.List>
    </Elements.HelpMenu>
    <Account />
  </Elements.Container>
);

export default Actions;
